import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { ConceptOption, ConceptEquivalenciesResponse } from '../../types';

interface ConceptSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

interface ConceptCache {
  data: ConceptOption[];
  lastUpdated: string;
  isLoading: boolean;
  error: string | null;
}

// Global cache to share concept data across all instances
let globalConceptCache: ConceptCache = {
  data: [],
  lastUpdated: '',
  isLoading: false,
  error: null
};

// Cache subscribers for state synchronization
const cacheSubscribers = new Set<() => void>();

const notifySubscribers = () => {
  cacheSubscribers.forEach(callback => callback());
};

const fetchConceptData = async (): Promise<void> => {
  if (globalConceptCache.isLoading) {
    return; // Prevent multiple simultaneous requests
  }

  globalConceptCache.isLoading = true;
  globalConceptCache.error = null;
  notifySubscribers();

  try {
    const response = await fetch('/api/v1/conceptos/equivalencias');
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ConceptEquivalenciesResponse = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to fetch concept data');
    }

    globalConceptCache.data = data.concepts;
    globalConceptCache.lastUpdated = data.last_updated || new Date().toISOString();
    globalConceptCache.error = null;
  } catch (error) {
    console.error('Error fetching concept data:', error);
    globalConceptCache.error = error instanceof Error ? error.message : 'Unknown error occurred';
    globalConceptCache.data = [];
  } finally {
    globalConceptCache.isLoading = false;
    notifySubscribers();
  }
};

export const ConceptSelector: React.FC<ConceptSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = "Seleccionar concepto...",
  error = false,
  helperText,
  size = 'small',
  fullWidth = true
}) => {
  const [localCache, setLocalCache] = useState<ConceptCache>(globalConceptCache);
  const [inputValue, setInputValue] = useState('');

  // Subscribe to cache updates
  useEffect(() => {
    const updateLocalCache = () => {
      setLocalCache({ ...globalConceptCache });
    };

    cacheSubscribers.add(updateLocalCache);

    // Initialize cache if empty and not loading
    if (globalConceptCache.data.length === 0 && !globalConceptCache.isLoading) {
      fetchConceptData();
    }

    return () => {
      cacheSubscribers.delete(updateLocalCache);
    };
  }, []);

  // Find the selected option based on the current value
  const selectedOption = useMemo(() => {
    if (!value || localCache.data.length === 0) return null;
    
    // Try to find by exact code match first
    let option = localCache.data.find(opt => opt.code === value);
    
    // If not found by code, try to find by full text match
    if (!option) {
      option = localCache.data.find(opt => opt.fullText === value);
    }
    
    // If still not found, try to find by description match
    if (!option) {
      option = localCache.data.find(opt => opt.description === value);
    }
    
    return option || null;
  }, [value, localCache.data]);

  // Filter options based on input value for real-time search
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) {
      return localCache.data;
    }

    const searchTerm = inputValue.toLowerCase();
    return localCache.data.filter(option =>
      option.code.toLowerCase().includes(searchTerm) ||
      option.description.toLowerCase().includes(searchTerm) ||
      option.fullText.toLowerCase().includes(searchTerm)
    );
  }, [localCache.data, inputValue]);

  const handleChange = (event: any, newValue: ConceptOption | null) => {
    if (newValue) {
      onChange(newValue.code);
    } else {
      onChange('');
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  // Show error state if there's a cache error
  if (localCache.error) {
    return (
      <Box>
        <TextField
          fullWidth={fullWidth}
          size={size}
          disabled
          placeholder="Error loading concepts"
          error={true}
          helperText="Failed to load concept data"
        />
        <Alert severity="error" sx={{ mt: 1 }}>
          <Typography variant="body2">
            Error loading concepts: {localCache.error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Autocomplete
      value={selectedOption}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={filteredOptions}
      getOptionLabel={(option) => option.fullText}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      loading={localCache.isLoading}
      disabled={disabled || localCache.isLoading}
      size={size}
      fullWidth={fullWidth}
      filterOptions={(x) => x} // Disable built-in filtering since we handle it manually
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={localCache.isLoading ? "Cargando conceptos..." : placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {localCache.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body2" component="div">
              <strong>{option.code}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.description}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText={
        localCache.isLoading 
          ? "Cargando conceptos..." 
          : inputValue.trim() 
            ? "No se encontraron conceptos que coincidan" 
            : "No hay conceptos disponibles"
      }
    />
  );
};

export default ConceptSelector;